// mng_front_sample/src/app.tsx
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { AvatarDropdown, AvatarName, Footer, SelectLang } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/login/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { Constants } from './util/constants';

const isDev = process.env.NODE_ENV === 'development';
const isDevOrTest = isDev || process.env.CI;
const LAST_PATH_KEY = 'last_path_after_login';

/** ACA のアプリケーション URL */
const API_BASE_URL =
  'https://ca-manage-backend.braveplant-bd140fe7.japanwest.azurecontainerapps.io';
/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.UserInfo;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.UserInfo | undefined>;
}> {
  // console.log('in getInitialState()');

  const fetchUserInfo = async () => {
    try {
      // console.log('in fetchUserInfo()');
      // ★ 重要: ベースURL + Cookie 同送設定は request の方で効かせる
      const msg = await queryCurrentUser({ skipErrorHandler: true });
      // console.log('out fetchUserInfo(). msg.data=' + JSON.stringify(msg.data, null, 2));
      return msg?.data;
    } catch {
      // console.log('catch error fetchUserInfo(). _error=' + JSON.stringify(_error, null, 2));

      // console.log('out fetchUserInfo(). return undefined.');
      return undefined;
    }
  };

  // ログインページでなければ、チェックを実行
  // TODO トークンが保持されている場合はログインをスキップするようにしたい
  const { location } = history;
  if (Constants.API.PATH.LOGIN !== location.pathname) {
    // console.log('in getInitialState(). call fetchUserInfo.');
    const currentUser = await fetchUserInfo();
    // console.log('out getInitialState(). currentUser=' + currentUser);
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  // console.log('out getInitialState(). non currentUser');
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// レイアウト設定（ProLayout）
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    // ヘッダーに追加したいアイコン
    actionsRender: () => [
      // <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    // ユーザー情報を表示
    avatarProps: {
      // アイコン
      // src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => (
        <AvatarDropdown>{avatarChildren}</AvatarDropdown>
      ),
    },
    // ユーザー名を背景に表示する場合は設定
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    // フッター
    footerRender: () => <Footer />,
    // 画面遷移
    onPageChange: () => {
      const { pathname } = history.location;
      const user = initialState?.currentUser;

      // login ページは常に素通し
      if (pathname === Constants.API.PATH.LOGIN) {
        if (history.action !== 'REPLACE') {
      history.replace(Constants.API.PATH.LOGIN);
    }
        return;
      }
      // 未ログインなら login へ
      if (!user) {
        const redirect = encodeURIComponent(pathname);
        history.replace(Constants.API.PATH.LOGIN);
        return;
      }
      localStorage.setItem(LAST_PATH_KEY, pathname);

      const isAdminPage = pathname.startsWith('/adminTop');
      const isAdmin = user.role_sid === Constants.USER.ROLE_SID.ADMIN;

      // 一般ユーザーが管理者ページを開こうとした場合
      if (isAdminPage && !isAdmin) {
        const schemaName = user.customer?.schema_name;

        if (schemaName) {
          history.replace(`/customerTop/${schemaName}/agencys/list`);
        } else {
          // 念のためのフォールバック
          history.replace(Constants.API.PATH.LOGIN);
        }
      }
    },

    // bgLayoutImgList: [
    //   {
    //     src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
    //     left: 85,
    //     bottom: 100,
    //     height: '303px',
    //   },
    //   {
    //     src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
    //     bottom: -68,
    //     right: -45,
    //     height: '303px',
    //   },
    //   {
    //     src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
    //     bottom: 0,
    //     left: 0,
    //     width: '331px',
    //   },
    // ],
    // links: isDevOrTest
    //   ? [
    //       <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
    //         <LinkOutlined />
    //         <span>OpenAPIドキュメント</span>
    //       </Link>,
    //     ]
    //   : [],

    //動的schema切替前
    // menuHeaderRender: undefined,
    // 画面遷移（menuクリック時）
    menuItemRender: (item, dom) => {
      const schemaName =
        initialState?.currentUser?.customer?.schema_name;

      if (!schemaName) {
        return <a onClick={() => history.push('/customerTop')}>{dom}</a>;
      }

      // ✅ menuKey があるものだけ schema 対象
      if (item.menuKey) {
        return (
          <a
            onClick={() =>
              history.push(
                `/customerTop/${schemaName}/${item.menuKey}/list`
              )
            }
          >
            {dom}
          </a>
        );
      }

      // adminTop 等
      if (item.path) {
        return <a onClick={() => history.push(item.path)}>{dom}</a>;
      }

      return dom;
    },

    // カスタム403画面
    // unAccessible: <div>unAccessible</div>,

    childrenRender: (children) => {
      // loading状態を追加する
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDevOrTest && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
    headerHeight: 100,
    headerTitleRender: () => (
      <a
        href="/"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          height: 100,
          paddingTop: 10,
        }}
      >
        <img
          src={(defaultSettings as any).logo ?? '/logo.png'}
          alt="logo"
          style={{ height: 50, width: 'auto', display: 'block' }}
        />
      </a>
    ),
  };
};

/**
 * @name request 設定（Umi Max）
 * - ACA を baseURL にして相対パスで呼ぶ（例：request('/auth/login', {...})）
 * - Cookie を **必ず**同送する（credentials + withCredentials）
 */
export const request: RequestConfig = {
  baseURL: API_BASE_URL,
  timeout: 30000,

  // ★ 重要: Axios 経由でも必ず Cookie を送る
  withCredentials: true,
  // 追加で毎リクエストに credentials を確実に付与（保険）
  requestInterceptors: [
    (url, options) => ({
      url,
      options: {
        ...options,
        withCredentials: true,
      },
    }),
  ],

  ...errorConfig,
};
